/**
 * Client-Side Rate Limiting Utilities
 *
 * Implements client-side rate limiting to prevent abuse and improve user experience.
 * Works in conjunction with server-side rate limiting for comprehensive protection.
 */

/**
 * Rate limiting configuration for different operations
 */
export interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
  blockDurationMs?: number;
  progressiveDelay?: boolean;
}

/**
 * Predefined rate limit configurations
 */
export const RATE_LIMIT_CONFIGS = {
  // AI Generation requests (expensive operations)
  AI_GENERATION: {
    maxRequests: 5,
    windowMs: 60 * 1000, // 1 minute
    blockDurationMs: 2 * 60 * 1000, // 2 minutes
    progressiveDelay: true,
  },

  // Form submissions
  FORM_SUBMISSION: {
    maxRequests: 10,
    windowMs: 60 * 1000, // 1 minute
    blockDurationMs: 30 * 1000, // 30 seconds
    progressiveDelay: true,
  },

  // Authentication attempts
  AUTH_ATTEMPTS: {
    maxRequests: 5,
    windowMs: 15 * 60 * 1000, // 15 minutes
    blockDurationMs: 15 * 60 * 1000, // 15 minutes
    progressiveDelay: true,
  },

  // File uploads
  FILE_UPLOAD: {
    maxRequests: 10,
    windowMs: 60 * 1000, // 1 minute
    blockDurationMs: 60 * 1000, // 1 minute
    progressiveDelay: false,
  },

  // Search queries
  SEARCH: {
    maxRequests: 30,
    windowMs: 60 * 1000, // 1 minute
    blockDurationMs: 10 * 1000, // 10 seconds
    progressiveDelay: false,
  },
} as const;

/**
 * Interface for tracking request attempts
 */
interface RequestAttempt {
  timestamp: number;
  count: number;
  blocked?: boolean;
  blockUntil?: number;
}

/**
 * Rate limiter class
 */
class RateLimiter {
  private attempts: Map<string, RequestAttempt[]> = new Map();
  private config: RateLimitConfig;
  private identifier: string;

  constructor(identifier: string, config: RateLimitConfig) {
    this.identifier = identifier;
    this.config = config;
  }

  /**
   * Check if request is allowed
   */
  isAllowed(): { allowed: boolean; retryAfter?: number; message?: string } {
    const now = Date.now();
    const key = this.identifier;

    // Get existing attempts
    let attempts = this.attempts.get(key) || [];

    // Clean up old attempts outside the window
    attempts = attempts.filter(
      attempt => now - attempt.timestamp < this.config.windowMs
    );

    // Check if currently blocked
    const latestAttempt = attempts[attempts.length - 1];
    if (
      latestAttempt?.blocked &&
      latestAttempt.blockUntil &&
      now < latestAttempt.blockUntil
    ) {
      const retryAfter = Math.ceil((latestAttempt.blockUntil - now) / 1000);
      return {
        allowed: false,
        retryAfter,
        message: `Rate limit exceeded. Please wait ${retryAfter} seconds before trying again.`,
      };
    }

    // Count requests in current window
    const requestCount = attempts.reduce(
      (sum, attempt) => sum + attempt.count,
      0
    );

    // Check if limit exceeded
    if (requestCount >= this.config.maxRequests) {
      const blockUntil =
        now + (this.config.blockDurationMs || this.config.windowMs);

      // Add blocked attempt
      attempts.push({
        timestamp: now,
        count: 1,
        blocked: true,
        blockUntil,
      });

      this.attempts.set(key, attempts);

      const retryAfter = Math.ceil(
        (this.config.blockDurationMs || this.config.windowMs) / 1000
      );
      return {
        allowed: false,
        retryAfter,
        message: `Rate limit exceeded. Please wait ${retryAfter} seconds before trying again.`,
      };
    }

    // Request is allowed
    attempts.push({
      timestamp: now,
      count: 1,
    });

    this.attempts.set(key, attempts);

    return { allowed: true };
  }

  /**
   * Get progressive delay based on attempt count
   */
  getProgressiveDelay(): number {
    if (!this.config.progressiveDelay) return 0;

    const attempts = this.attempts.get(this.identifier) || [];
    const recentAttempts = attempts.filter(
      attempt => Date.now() - attempt.timestamp < this.config.windowMs
    );

    const attemptCount = recentAttempts.length;

    // Progressive delay: 0ms, 1s, 2s, 4s, 8s, etc.
    return Math.min(Math.pow(2, attemptCount - 1) * 1000, 30000);
  }

  /**
   * Reset rate limit for identifier
   */
  reset(): void {
    this.attempts.delete(this.identifier);
  }

  /**
   * Get current status
   */
  getStatus(): {
    requestCount: number;
    remainingRequests: number;
    resetTime: number;
    isBlocked: boolean;
  } {
    const now = Date.now();
    const attempts = this.attempts.get(this.identifier) || [];

    // Filter recent attempts
    const recentAttempts = attempts.filter(
      attempt => now - attempt.timestamp < this.config.windowMs
    );

    const requestCount = recentAttempts.reduce(
      (sum, attempt) => sum + attempt.count,
      0
    );
    const remainingRequests = Math.max(
      0,
      this.config.maxRequests - requestCount
    );

    // Calculate reset time (when window expires)
    const oldestAttempt = recentAttempts[0];
    const resetTime = oldestAttempt
      ? oldestAttempt.timestamp + this.config.windowMs
      : now;

    // Check if blocked
    const latestAttempt = attempts[attempts.length - 1];
    const isBlocked =
      latestAttempt?.blocked &&
      latestAttempt.blockUntil &&
      now < latestAttempt.blockUntil;

    return {
      requestCount,
      remainingRequests,
      resetTime,
      isBlocked: !!isBlocked,
    };
  }
}

/**
 * Global rate limiter instances
 */
const rateLimiters: Map<string, RateLimiter> = new Map();

/**
 * Get or create rate limiter for operation
 */
const getRateLimiter = (operation: string, userId?: string): RateLimiter => {
  const config =
    RATE_LIMIT_CONFIGS[operation as keyof typeof RATE_LIMIT_CONFIGS];
  if (!config) {
    throw new Error(`Unknown rate limit operation: ${operation}`);
  }

  // Create identifier (include user ID for user-specific limits)
  const identifier = userId ? `${operation}:${userId}` : operation;

  if (!rateLimiters.has(identifier)) {
    rateLimiters.set(identifier, new RateLimiter(identifier, config));
  }

  return rateLimiters.get(identifier)!;
};

/**
 * Check if operation is rate limited
 */
export const checkRateLimit = (
  operation: keyof typeof RATE_LIMIT_CONFIGS,
  userId?: string
): { allowed: boolean; retryAfter?: number; message?: string } => {
  try {
    const limiter = getRateLimiter(operation, userId);
    return limiter.isAllowed();
  } catch (error) {
    console.error("Rate limit check error:", error);
    // Allow request if rate limiting fails
    return { allowed: true };
  }
};

/**
 * Apply progressive delay before operation
 */
export const applyProgressiveDelay = async (
  operation: keyof typeof RATE_LIMIT_CONFIGS,
  userId?: string
): Promise<void> => {
  try {
    const limiter = getRateLimiter(operation, userId);
    const delay = limiter.getProgressiveDelay();

    if (delay > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  } catch (error) {
    console.error("Progressive delay error:", error);
    // Continue without delay if error occurs
  }
};

/**
 * Rate limit wrapper for async functions
 */
export const withRateLimit = async <T>(
  operation: keyof typeof RATE_LIMIT_CONFIGS,
  fn: () => Promise<T>,
  userId?: string
): Promise<T> => {
  // Check rate limit
  const limitCheck = checkRateLimit(operation, userId);

  if (!limitCheck.allowed) {
    throw new Error(limitCheck.message || "Rate limit exceeded");
  }

  // Apply progressive delay
  await applyProgressiveDelay(operation, userId);

  // Execute function
  return fn();
};

/**
 * Get rate limit status for operation
 */
export const getRateLimitStatus = (
  operation: keyof typeof RATE_LIMIT_CONFIGS,
  userId?: string
): {
  requestCount: number;
  remainingRequests: number;
  resetTime: number;
  isBlocked: boolean;
} => {
  try {
    const limiter = getRateLimiter(operation, userId);
    return limiter.getStatus();
  } catch (error) {
    console.error("Rate limit status error:", error);
    return {
      requestCount: 0,
      remainingRequests: 999,
      resetTime: Date.now(),
      isBlocked: false,
    };
  }
};

/**
 * Reset rate limit for operation
 */
export const resetRateLimit = (
  operation: keyof typeof RATE_LIMIT_CONFIGS,
  userId?: string
): void => {
  try {
    const limiter = getRateLimiter(operation, userId);
    limiter.reset();
  } catch (error) {
    console.error("Rate limit reset error:", error);
  }
};

/**
 * Clean up old rate limit data (call periodically)
 */
export const cleanupRateLimitData = (): void => {
  const now = Date.now();
  const maxAge = 24 * 60 * 60 * 1000; // 24 hours

  for (const [key, limiter] of rateLimiters.entries()) {
    const status = limiter.getStatus();

    // Remove old limiters that haven't been used recently
    if (now - status.resetTime > maxAge) {
      rateLimiters.delete(key);
    }
  }
};

// Global cleanup interval reference
let rateLimitCleanupInterval: NodeJS.Timeout | null = null;

/**
 * Initialize rate limiting (call on app startup)
 */
export const initializeRateLimiting = (): void => {
  // Clear existing interval if any
  if (rateLimitCleanupInterval) {
    clearInterval(rateLimitCleanupInterval);
  }

  // Set up periodic cleanup
  rateLimitCleanupInterval = setInterval(cleanupRateLimitData, 60 * 60 * 1000); // Every hour
};

/**
 * Cleanup rate limiting (call on app shutdown)
 */
export const cleanupRateLimiting = (): void => {
  if (rateLimitCleanupInterval) {
    clearInterval(rateLimitCleanupInterval);
    rateLimitCleanupInterval = null;
  }
  rateLimiters.clear();
};
